import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import MediaEditModal from './MediaEditModal';
import MediaGcDialog from './MediaGcDialog';
// File extensions that get the editor (focal / crop / max-width). Everything
// else (svg, gif, mp4, webm, mov, m4v) bypasses the modal and uploads as-is.
const EDITABLE_EXTS = new Set(['jpg', 'jpeg', 'png', 'webp']);
function isEditable(file) {
    const dot = file.name.lastIndexOf('.');
    const ext = dot >= 0 ? file.name.slice(dot + 1).toLowerCase() : '';
    return EDITABLE_EXTS.has(ext);
}
export default function MediaBrowser({ accept = 'all', onPick, onClose, pageMode = false }) {
    const [items, setItems] = useState(null);
    const [filter, setFilter] = useState(accept === 'all' ? 'all' : accept);
    const [search, setSearch] = useState('');
    const [error, setError] = useState(null);
    const [warning, setWarning] = useState(null);
    const [busy, setBusy] = useState(false);
    const [selected, setSelected] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const [editorOpen, setEditorOpen] = useState(false);
    const [gcOpen, setGcOpen] = useState(false);
    // Multi-file upload queue: raster files awaiting per-file editor pass.
    const [uploadQueue, setUploadQueue] = useState([]);
    const [uploadQueueIdx, setUploadQueueIdx] = useState(0);
    const [uploadQueueTotal, setUploadQueueTotal] = useState(0);
    const [failed, setFailed] = useState(new Set());
    const fileInput = useRef(null);
    const pickEnabled = !!onPick;
    async function refresh() {
        setError(null);
        try {
            const { media } = await api.listMedia();
            setItems(media);
        }
        catch (e) {
            setError(e?.message ?? 'Failed to load media');
        }
    }
    useEffect(() => { refresh(); }, []);
    useEffect(() => {
        if (pageMode || !onClose)
            return;
        function onKey(e) { if (e.key === 'Escape' && !busy)
            onClose(); }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [busy, onClose, pageMode]);
    const visible = useMemo(() => {
        if (!items)
            return [];
        const q = search.trim().toLowerCase();
        return items.filter((m) => {
            if (filter !== 'all' && m.kind !== filter)
                return false;
            if (q && !m.rel.toLowerCase().includes(q) && !m.alt.toLowerCase().includes(q))
                return false;
            return true;
        });
    }, [items, filter, search]);
    // Upload entry point. Splits incoming files into:
    //   - non-editable (svg/gif/video) → upload immediately, no modal
    //   - editable raster              → push onto a queue; the editor modal
    //                                    opens for each in turn so the user can
    //                                    set focal/crop + max-width before send.
    async function uploadFiles(files) {
        setError(null);
        setWarning(null);
        const arr = Array.from(files);
        const passthru = arr.filter((f) => !isEditable(f));
        const editable = arr.filter((f) => isEditable(f));
        if (passthru.length > 0) {
            setBusy(true);
            const allWarnings = [];
            try {
                for (const f of passthru) {
                    const r = await api.uploadMedia(f);
                    if (r.warnings && r.warnings.length) {
                        for (const w of r.warnings)
                            allWarnings.push(`${f.name}: ${w}`);
                    }
                }
                await refresh();
                if (allWarnings.length)
                    setWarning(allWarnings.join(' · '));
            }
            catch (e) {
                setError(e?.message ?? 'Upload failed');
            }
            finally {
                setBusy(false);
            }
        }
        if (editable.length > 0) {
            setUploadQueue(editable);
            setUploadQueueIdx(0);
            setUploadQueueTotal(editable.length);
        }
    }
    // Called when the modal completes (or cancels) for the current queued file.
    async function onQueueDone() {
        setUploadQueueIdx((i) => i + 1);
        await refresh();
    }
    function onQueueCancel() {
        // Skip just this one file, move to next.
        setUploadQueueIdx((i) => i + 1);
    }
    async function onSkipRest() {
        // Upload remaining queued files with no ops, no modal.
        const remaining = uploadQueue.slice(uploadQueueIdx + 1);
        setUploadQueue([]);
        setUploadQueueIdx(0);
        setUploadQueueTotal(0);
        if (remaining.length === 0) {
            await refresh();
            return;
        }
        setBusy(true);
        try {
            for (const f of remaining) {
                await api.uploadMedia(f);
            }
            await refresh();
        }
        catch (e) {
            setError(e?.message ?? 'Upload failed');
        }
        finally {
            setBusy(false);
        }
    }
    // When the queue index runs past the end, drain it.
    useEffect(() => {
        if (uploadQueueTotal > 0 && uploadQueueIdx >= uploadQueueTotal) {
            setUploadQueue([]);
            setUploadQueueIdx(0);
            setUploadQueueTotal(0);
        }
    }, [uploadQueueIdx, uploadQueueTotal]);
    function onFilePick(e) {
        if (e.target.files?.length)
            uploadFiles(e.target.files);
        e.target.value = '';
    }
    function onDrop(e) {
        e.preventDefault();
        setDragOver(false);
        if (e.dataTransfer?.files?.length)
            uploadFiles(e.dataTransfer.files);
    }
    async function deleteItem(item) {
        if (!window.confirm(`Delete ${item.rel} and its variants? This cannot be undone.`))
            return;
        setBusy(true);
        try {
            await api.deleteMedia(item.rel);
            if (selected?.rel === item.rel)
                setSelected(null);
            await refresh();
        }
        catch (e) {
            setError(e?.message ?? 'Delete failed');
        }
        finally {
            setBusy(false);
        }
    }
    async function saveAlt(item, alt, tags) {
        setBusy(true);
        try {
            await api.patchMedia(item.rel, { alt, tags });
            await refresh();
            setSelected({ ...item, alt, tags });
        }
        catch (e) {
            setError(e?.message ?? 'Save failed');
        }
        finally {
            setBusy(false);
        }
    }
    const body = (_jsxs(_Fragment, { children: [_jsxs("div", { className: pageMode ? 'media-page-head' : 'modal-head', children: [!pageMode && _jsx("div", { className: "modal-title", children: "Media library" }), _jsxs("div", { className: "media-toolbar", children: [_jsx("input", { type: "search", placeholder: "Search alt text or filename\u2026", value: search, onChange: (e) => setSearch(e.target.value) }), _jsx("div", { className: "filter-pills", children: ['all', 'image', 'video', 'svg'].map((f) => (_jsx("button", { className: filter === f ? 'pill active' : 'pill', onClick: () => setFilter(f), disabled: accept !== 'all' && accept !== f, children: f }, f))) }), _jsx("button", { onClick: () => fileInput.current?.click(), disabled: busy, children: busy ? 'Working…' : '↥ Upload' }), _jsx("input", { ref: fileInput, type: "file", multiple: true, style: { display: 'none' }, onChange: onFilePick }), _jsx("button", { className: "ghost", onClick: () => setGcOpen(true), disabled: busy, title: "Clean unused media", children: "\uD83D\uDDD1 Clean" }), !pageMode && onClose && (_jsx("button", { className: "ghost", onClick: onClose, disabled: busy, children: "Close" }))] })] }), error && _jsx("div", { className: "err small pad-y", style: { paddingLeft: '1rem' }, children: error }), warning && (_jsxs("div", { className: "warn small pad-y", style: { paddingLeft: '1rem' }, role: "status", children: ["\u26A0 ", warning, " ", _jsx("button", { className: "ghost small", onClick: () => setWarning(null), children: "dismiss" })] })), _jsxs("div", { className: `${pageMode ? 'media-page-body' : 'modal-body'} media-grid-wrap${dragOver ? ' drag-over' : ''}`, onDragOver: (e) => { e.preventDefault(); setDragOver(true); }, onDragLeave: () => setDragOver(false), onDrop: onDrop, children: [!items && _jsx("div", { className: "muted pad", children: "Loading\u2026" }), items && visible.length === 0 && (_jsx("div", { className: "muted pad", children: "No media. Drop files here or click Upload." })), items && (_jsx("div", { className: "media-grid", children: visible.map((m) => {
                            const broken = failed.has(m.rel);
                            const showImg = (m.kind === 'image' || m.kind === 'svg') && !broken;
                            return (_jsxs("div", { className: `media-cell${selected?.rel === m.rel ? ' selected' : ''}`, onClick: () => setSelected(m), onDoubleClick: () => pickEnabled && onPick(m.rel, m), children: [_jsx("div", { className: "media-thumb", "data-kind": m.kind, children: showImg ? (_jsx("img", { src: m.thumb_url, alt: m.alt || basename(m.rel), loading: "lazy", onError: () => setFailed((s) => { if (s.has(m.rel))
                                                return s; const n = new Set(s); n.add(m.rel); return n; }) })) : (_jsx("div", { className: "media-icon", title: broken ? 'Image failed to load' : '', children: m.kind === 'video' ? '🎬' : broken ? '⚠' : '📄' })) }), _jsxs("div", { className: "media-cell-meta", title: m.rel, children: [_jsx("span", { className: "media-cell-name", children: basename(m.rel) }), _jsx("span", { className: "muted small", children: prettyBytes(m.bytes) })] })] }, m.rel));
                        }) }))] }), selected && (_jsx(MediaDetails, { item: selected, onClose: () => setSelected(null), onPick: pickEnabled ? () => onPick(selected.rel, selected) : undefined, onDelete: () => deleteItem(selected), onEdit: () => setEditorOpen(true), onSave: (alt, tags) => saveAlt(selected, alt, tags) }, selected.rel)), selected && editorOpen && (_jsx(MediaEditModal, { source: { kind: 'remote', item: selected }, onDone: async (updated) => {
                    setEditorOpen(false);
                    setSelected(updated);
                    await refresh();
                }, onCancel: () => setEditorOpen(false) })), uploadQueueTotal > 0 && uploadQueueIdx < uploadQueueTotal && (_jsx(MediaEditModal, { source: { kind: 'file', file: uploadQueue[uploadQueueIdx] }, queueIndex: uploadQueueIdx, queueTotal: uploadQueueTotal, onSkipRest: uploadQueueTotal - uploadQueueIdx > 1 ? onSkipRest : undefined, onDone: onQueueDone, onCancel: onQueueCancel }, `upload-${uploadQueueIdx}`)), gcOpen && (_jsx(MediaGcDialog, { onClose: () => setGcOpen(false), onChanged: async () => { await refresh(); setSelected(null); } }))] }));
    if (pageMode) {
        return _jsx("div", { className: "media-page", children: body });
    }
    return (_jsx("div", { className: "modal-backdrop", onClick: (e) => { if (e.target === e.currentTarget && !busy && onClose)
            onClose(); }, children: _jsx("div", { className: "modal media-modal", children: body }) }));
}
function MediaDetails({ item, onClose, onPick, onDelete, onEdit, onSave, }) {
    const [alt, setAlt] = useState(item.alt);
    const [tags, setTags] = useState(item.tags.join(', '));
    const [usages, setUsages] = useState(null);
    const [usagesLoading, setUsagesLoading] = useState(false);
    const [usagesOpen, setUsagesOpen] = useState(false);
    async function toggleUsages() {
        if (usagesOpen) {
            setUsagesOpen(false);
            return;
        }
        setUsagesOpen(true);
        if (usages)
            return;
        setUsagesLoading(true);
        try {
            const r = await api.mediaUsages(item.rel);
            setUsages(r.usages);
        }
        catch {
            setUsages([]);
        }
        finally {
            setUsagesLoading(false);
        }
    }
    const isImage = item.kind === 'image';
    return (_jsxs("div", { className: "media-detail", children: [_jsxs("div", { className: "media-detail-head", children: [_jsxs("div", { children: [_jsx("div", { className: "media-detail-name", children: basename(item.rel) }), _jsxs("div", { className: "muted small", children: [item.rel, " \u00B7 ", prettyBytes(item.bytes), item.width ? ` · ${item.width}×${item.height}` : ''] })] }), _jsx("button", { className: "ghost", onClick: onClose, children: "\u00D7" })] }), _jsxs("div", { className: "media-detail-body", children: [_jsxs("label", { className: "field", children: [_jsx("span", { children: "Alt text" }), _jsx("input", { type: "text", value: alt, onChange: (e) => setAlt(e.target.value), placeholder: "Describe the image for screen readers" })] }), _jsxs("label", { className: "field", children: [_jsx("span", { children: "Tags (comma-separated)" }), _jsx("input", { type: "text", value: tags, onChange: (e) => setTags(e.target.value), placeholder: "hero, food, brand" })] }), isImage && (_jsx("button", { className: "ghost", onClick: onEdit, children: "\u270F\uFE0F Edit image (focal / crop / rotate)" })), _jsxs("details", { open: usagesOpen, onClick: (e) => { if (e.target.tagName === 'SUMMARY') {
                            e.preventDefault();
                            toggleUsages();
                        } }, children: [_jsx("summary", { children: "Where used" }), usagesLoading && _jsx("div", { className: "muted small", children: "Loading\u2026" }), !usagesLoading && usages && usages.length === 0 && _jsx("div", { className: "muted small", children: "No references found." }), !usagesLoading && usages && usages.length > 0 && (_jsx("ul", { className: "usage-list", children: usages.map((u, i) => (_jsxs("li", { children: [_jsxs("code", { children: ["/", u.slug === 'home' ? '' : u.slug] }), _jsx("span", { className: "muted small", children: u.kind }), _jsx("span", { className: "muted small", children: u.detail })] }, i))) }))] })] }), _jsxs("div", { className: "media-detail-foot", children: [_jsx("button", { className: "ghost danger", onClick: onDelete, children: "Delete" }), _jsx("button", { className: "ghost", onClick: () => onSave(alt, tags.split(',').map((t) => t.trim()).filter(Boolean)), children: "Save" }), onPick && _jsx("button", { onClick: onPick, children: "Use this" })] })] }));
}
function basename(rel) {
    const i = rel.lastIndexOf('/');
    return i < 0 ? rel : rel.slice(i + 1);
}
function prettyBytes(n) {
    if (n < 1024)
        return n + 'B';
    if (n < 1024 * 1024)
        return (n / 1024).toFixed(0) + 'KB';
    return (n / 1024 / 1024).toFixed(1) + 'MB';
}
